import {
  RightOutlined,
  SearchOutlined} from '@ant-design/icons';
import { Checkbox, Input, List } from 'antd';
import React, { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

import AvatarEmpty from '../AvatarEmpty';
import { AccountItemList } from './AccountItemList';
import {
  AvatarCustom,
  ContentList, StyledTableSkeleton, TableFade,
} from './Styles';
import TableSkeleton from './TableSkeleton';

type Props = {
  fetching: boolean;
  list?: AccountItemList[];
  withSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (search: string, page?: number, pageSize?: number) => void;
  onItemSelected: (item: AccountItemList) => void;
  headerButtonText?: string;
  onHeaderButtonClick?: () => void;
  selectable?: boolean;
  pagination?: Pagination;
  setPagination?: (newPagination: Pagination) => void;
  skeletonLength?: number;
  fixedHeight?: number;
  showBottomFade?: boolean;
  showSecondCharOnAvatar?: boolean;
}

const AccountsList: React.FC<Props> = ({
  fetching,
  list,
  searchPlaceholder,
  withSearch = true,
  onSearch,
  onItemSelected,
  selectable,
  pagination,
  setPagination,
  skeletonLength,
  fixedHeight,
  showSecondCharOnAvatar,
  showBottomFade = true
}) => {
  const [itemClicked, setItemClicked] = useState<boolean>(false);
  const [showTableFade, setShowTableFade] = useState<boolean>(showBottomFade);
  const [value, setValue] = useState('');  

  useEffect(() => {
    if (!fetching) {
      setItemClicked(false);
    }
  }, [fetching]);

  useEffect(() => {
    setValue('');
  }, [searchPlaceholder]);
  
  const handleItemClicked = (item: AccountItemList) => {
    setItemClicked(true);
    onItemSelected(item);
  };

  const handleChange = (event: any) => {
    setValue(event.target.value);
    onSearch && onSearch(event.target.value);
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    if (pagination && setPagination) {
      setPagination({...pagination, current_page: page, per_page: pageSize});
    }
  }

  const content = (
    <ContentList
      style={{height: `${fixedHeight}px` || '335px'}}
      className={searchPlaceholder ? 'withPadding' : ''}
    >
      <List
        itemLayout="horizontal"
        dataSource={list}
        pagination={pagination ? ({
          current: pagination.current_page,
          pageSize: pagination.per_page,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['50', '100'],
          onChange: handlePaginationChange
        }): undefined}
        renderItem={(item) => (
          <List.Item onClick={() => {!selectable && handleItemClicked(item)}}>
            <List.Item.Meta
              style={{ alignItems: 'center' }}
              avatar={
                item.logo ? <AvatarCustom src={item.logo} /> :
                <AvatarEmpty char1={item.name.charAt(0)} char2={showSecondCharOnAvatar ? item.name.split(' ')[1].charAt(0) : undefined} />
              }
              title={item.name}
              description={item.role}
            />
            { !selectable ?
              <RightOutlined style={{color: 'rgba(0, 0, 0, 0.25)'}}/> :
              <Checkbox
                data-testid={`check-item-list-${item.id}`}
                checked={item.selected}
                onClick={() => onItemSelected(item)}
              />
            }
          </List.Item>
        )}
      />
    </ContentList>
  );

  return (
    <>
      {withSearch && <Input
        size="large"
        placeholder={searchPlaceholder}
        prefix={<SearchOutlined />}
        allowClear
        style={{ marginBottom: 15 }}
        onChange={handleChange}
        disabled={fetching}
        value={value}
      />}
      {fetching || itemClicked ? (
        <StyledTableSkeleton>
          <TableSkeleton rows={skeletonLength ? skeletonLength : pagination ? 5 : 6} />
        </StyledTableSkeleton>
      ) : (
        !fixedHeight ? <Scrollbars
          autoHeight
          autoHide
          autoHeightMin={pagination ? 335 : 300}
          autoHeightMax={pagination? 335 : 300}
          style={{
            marginRight: -12,
            width: 'auto'
          }}
          onScrollFrame={(values) => (
            (showTableFade && values.top === 1) ?
              setShowTableFade(false) :
              setShowTableFade(true)
          )}
        >
          {content}
        </Scrollbars> : content
      )}
      {showTableFade && <TableFade />}
    </>
  );
};

export default AccountsList;
